import { Popover } from '@headlessui/react';
import { mainClient } from 'apollo/client';
import { DetailPageClientContext } from 'components/desktop/detail';
import Breadcrumb from 'components/desktop/shared/breadcrumb';
import ToastStack from 'components/desktop/shared/toast-stack';
import {
  IconCalendar,
  IconCopy,
  IconDelete,
  IconDirectionDown,
  IconLocationFill,
  IconNoteEditAlt,
  IconPanHorizontal,
  IconPanVertical,
  IconStairs,
  IconTagOutline,
  IconUndo,
  IconVectorSquareClose,
} from 'components/icons';
import { useAuthenticationContext } from 'components/providers/authentication-provider';
import ConfirmationModal from 'components/shared/comfirmation-modal';
import { SITE_MAP } from 'globalConstants';
import {
  DELETE_LIST_REAL_ESTATES,
  DeleteListRealEstatesData,
  DeleteListRealEstatesVariables,
  UPDATE_LIST_TRADING_REAL_ESTATE,
  UpdateListTradingRealEstatesQueryData,
  UpdateListTradingRealEstatesQueryVariables,
} from 'graphql/main/mutations';
import { GET_REAL_ESTATE_DETAILS, TradingStatus } from 'graphql/main/queries';
import { useConfirmationModal, useFlexMutation, useToast, useTranslation } from 'hooks';
import { isEmpty, isEqual, isNil, lt, map, some, toNumber } from 'lodash';
import moment from 'moment-timezone';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { DetailPageContext } from 'pages/[ket-qua-tim-kiem]/[chi-tiet-tin-rao]';
import { Fragment, useContext } from 'react';
import { convertNumberToVietnameseCurrencyShortString, timeSince } from 'utils';

const MainContent = () => {
  const { push } = useRouter();
  const translation = useTranslation();
  const { currentUser } = useAuthenticationContext();
  const { realEstate } = useContext(DetailPageContext).pageData ?? {};
  const { realEstate: realEstateClient } = useContext(DetailPageClientContext).pageData ?? {};
  const {
    area,
    floorCount,
    width,
    length,
    creator,
    id,
    breadScrumbs,
    approvalStatus,
    endShowTime,
    isForSell,
    price,
    postTitle,
    fullAddress,
    createdAt,
    realEstateId,
  } = realEstate ?? {};
  const { tradingStatus } = realEstateClient ?? {};
  const stack = [
    {
      icon: <IconVectorSquareClose className='h-[18px] w-[18px]' />,
      content: area?.toLocaleString(),
      suffix: 'm\u00B2',
    },
    {
      icon: <IconPanHorizontal className='h-[18px] w-[18px]' />,
      content: width?.toLocaleString(),
      suffix: 'm',
    },
    {
      icon: <IconPanVertical className='h-[18px] w-[18px]' />,
      content: length?.toLocaleString(),
      suffix: 'm',
    },
    {
      icon: <IconStairs className='h-[18px] w-[18px]' />,
      content: floorCount?.toLocaleString(),
      suffix: 'tầng',
    },
  ];
  const approvalStatusElement = {
    approved: {
      iconClassname: 'bg-[url("/icons/approved.svg")]',
      colorClassname: 'text-[#22C55e]',
    },
    pending: {
      iconClassname: 'bg-[url("/icons/pending.svg")]',
      colorClassname: 'text-[#2c6cd2]',
    },
    rejected: {
      iconClassname: 'bg-[url("/icons/rejected.svg")]',
      colorClassname: 'text-[#eb0000]',
    },
  };
  const tradingStatusOptions = [TradingStatus.trading, TradingStatus.stop];
  const [updateListTradingRealEstates, { error: updateListTradingRealEstatesError }] =
    useFlexMutation<
      UpdateListTradingRealEstatesQueryData,
      UpdateListTradingRealEstatesQueryVariables
    >('main', UPDATE_LIST_TRADING_REAL_ESTATE);
  const [deleteListRealEstates, { error: deleteListRealEstatesError }] = useFlexMutation<
    DeleteListRealEstatesData,
    DeleteListRealEstatesVariables
  >('main', DELETE_LIST_REAL_ESTATES);
  const isMyPost = isEqual(currentUser?.id, creator?.id);
  const { toasts, addToast, removeToast } = useToast();
  const {
    showing: showingChangeTradingStatusConfirmModal,
    data: changeTradingStatusConfirmModalData,
    toggle: toggleChangeTradingStatusConfirmModal,
  } = useConfirmationModal();
  const {
    showing: showingDeleteListRealEstatesConfirmModal,
    toggle: toggleDeleteListRealEstatesConfirmModal,
  } = useConfirmationModal();

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text);
    addToast('success', 'Sao chép mã tin đăng thành công');
  };
  const confirmChangeTradingStatus = async () => {
    toggleChangeTradingStatusConfirmModal();
    if (id) {
      await updateListTradingRealEstates({
        variables: {
          realEstateIds: [id],
          tradingStatus: changeTradingStatusConfirmModalData as TradingStatus,
        },
        onCompleted: (data) => {
          mainClient.refetchQueries({ include: [GET_REAL_ESTATE_DETAILS] });
          if (!isEmpty(data.updateListTradingRealEstates)) {
            addToast('success', 'Chuyển trạng thái tin đăng thành công');
          } else {
            addToast('error', 'Chuyển trạng thái tin đăng thất bại');
          }
          if (updateListTradingRealEstatesError) {
            addToast(
              'error',
              `Chuyển trạng thái tin đăng thất bại. Đã có lỗi xảy ra: ${updateListTradingRealEstatesError.message}`,
            );
          }
        },
        onError: (error) => {
          addToast(
            'error',
            `Chuyển trạng thái tin đăng thất bại. Đã có lỗi xảy ra: ${error.message}`,
          );
        },
      });
    } else {
      addToast('error', 'Chuyển trạng thái tin đăng thất bại');
    }
  };
  const confirmDeleteRealEstate = async () => {
    toggleDeleteListRealEstatesConfirmModal();
    if (id) {
      await deleteListRealEstates({
        variables: {
          realEstateIds: [id],
        },
        onCompleted: (data) => {
          push('/');
          if (!isEmpty(data.deleteListRealEstates)) {
            addToast('success', 'Xoá tin đăng thành công');
          } else {
            addToast('error', 'Xoá tin đăng thất bại');
          }
          if (deleteListRealEstatesError) {
            addToast(
              'error',
              `Xoá tin đăng thất bại. Đã có lỗi xảy ra: ${deleteListRealEstatesError.message}`,
            );
          }
        },
        onError: (error) => {
          addToast('error', `Xoá tin đăng thất bại. Đã có lỗi xảy ra: ${error.message}`);
        },
      });
    } else {
      addToast('error', 'Xoá tin đăng thất bại');
    }
  };

  return (
    <div id='main-content' className='flex flex-col space-y-[16px]'>
      <Breadcrumb
        breadcrumbs={[
          { name: 'Trang chủ', slug: '' },
          ...(breadScrumbs ?? []),
          { name: postTitle },
        ]}
      />
      {isMyPost && (
        <div className='flex min-h-[44px] flex-wrap items-center justify-end xl:m-[-20px] m-[-16px]'>
          {approvalStatus && (
            <div className='flex items-center space-x-[6px] p-[16px] xl:p-[20px]'>
              <div
                className={`h-[20px] min-w-[20px] bg-cover bg-center bg-no-repeat ${(
                  approvalStatusElement as any
                )[approvalStatus]?.iconClassname}`}
              />
              <span
                className={`text-[12px] font-[600] leading-[15px] ${(approvalStatusElement as any)[
                  approvalStatus
                ]?.colorClassname}`}
              >
                {(translation.major.approvalStatus as any)[approvalStatus]}
              </span>
            </div>
          )}
          {endShowTime && (
            <Link
              href={
                !lt(endShowTime, moment().unix)
                  ? `/${SITE_MAP.UPDATE_REAL_ESTATE.INDEX}/${id}`
                  : '#'
              }
              className='flex items-center space-x-[6px] p-[16px] xl:p-[20px]'
            >
              {lt(endShowTime, moment().unix) ? (
                <IconUndo className='h-[20px] min-w-[20px] text-primary' />
              ) : (
                <IconNoteEditAlt className='h-[20px] min-w-[20px] text-primary' />
              )}
              <span className='text-[12px] leading-[15px]'>
                {lt(endShowTime, moment().unix) ? 'Gia hạn tin' : 'Sửa tin đăng'}
              </span>
            </Link>
          )}
          {tradingStatus && (
            <Fragment>
              <Popover className='relative'>
                <Popover.Button
                  type='button'
                  className='flex items-center space-x-[6px] p-[16px] xl:p-[20px]'
                >
                  <span className='text-[12px] leading-[15px]'>
                    {(translation.major.tradingStatus as any)[tradingStatus]}
                  </span>
                  <IconDirectionDown className='h-[20px] min-w-[20px] text-text2' />
                </Popover.Button>
                <Popover.Panel className='absolute top-[calc(100%+16px)] flex min-w-[220px] flex-col space-y-[16px] rounded-[8px] bg-paper py-[16px] shadow-4'>
                  {map(tradingStatusOptions, (tradingStatusOption, tradingStatusOptionIndex) => (
                    <button
                      key={tradingStatusOptionIndex}
                      disabled={isEqual(tradingStatusOption, tradingStatus)}
                      type='button'
                      className='flex h-[32px] items-center px-[16px] transition duration-[200ms] ease-in-out hover:bg-secondary disabled:opacity-50 disabled:hover:bg-transparent'
                      onClick={() => toggleChangeTradingStatusConfirmModal(tradingStatusOption)}
                    >
                      <span>{(translation.major.tradingStatus as any)[tradingStatusOption]}</span>
                    </button>
                  ))}
                </Popover.Panel>
              </Popover>
              <ConfirmationModal
                showing={showingChangeTradingStatusConfirmModal}
                title='Bạn có muốn chuyển trạng thái tin đăng?'
                content={`Sau khi chuyển, trạng thái giao dịch sẽ sang “${
                  (translation.major.tradingStatus as any)[changeTradingStatusConfirmModalData]
                }”`}
                onConfirm={confirmChangeTradingStatus}
                onCancel={toggleChangeTradingStatusConfirmModal}
              />
            </Fragment>
          )}
          <button
            type='button'
            className='flex items-center space-x-[6px] text-alert p-[16px] xl:p-[20px]'
            onClick={() => toggleDeleteListRealEstatesConfirmModal(id)}
          >
            <IconDelete className='h-[20px] min-w-[20px]' />
            <span className='text-[12px] leading-[15px]'>Xoá tin đăng</span>
          </button>
          <ConfirmationModal
            showing={showingDeleteListRealEstatesConfirmModal}
            title='Bạn có muốn xoá tin đăng?'
            content='Sau khi xoá, tin đăng sẽ bị mất'
            onConfirm={confirmDeleteRealEstate}
            onCancel={toggleDeleteListRealEstatesConfirmModal}
          />
        </div>
      )}
      {postTitle && (
        <h1 className='text-[24px] font-[700] uppercase leading-[30px]'>{postTitle}</h1>
      )}
      {fullAddress && (
        <Fragment>
          <div className='flex min-h-[24px] items-center space-x-[6px]'>
            <IconLocationFill className='mt-[1px] h-[18px] min-w-[18px] text-text2' />
            <span>{fullAddress}</span>
          </div>
          <div className='h-[1px] bg-stroke' />
        </Fragment>
      )}
      {!isNaN(toNumber(price)) && (
        <Fragment>
          <div className='flex items-center space-x-[10px]'>
            <IconTagOutline className='h-[20px] min-w-[20px] text-alert' />
            <span>Mức giá:</span>
            <span className='text-[24px] font-[600] leading-[30px] text-alert'>
              {isNil(price) || isEqual(toNumber(price), 0)
                ? 'Thương lượng'
                : `${convertNumberToVietnameseCurrencyShortString(toNumber(price))}${
                    isForSell ? '' : '/tháng'
                  }`}
            </span>
          </div>
          <div className='h-[1px] bg-stroke' />
        </Fragment>
      )}
      {some(stack, (item) => item.content) && (
        <Fragment>
          <div className='flex min-h-[24px] items-center space-x-[32px]'>
            {map(
              stack,
              (stackItem, stackItemIndex) =>
                stackItem.content && (
                  <div key={stackItemIndex} className='flex items-center space-x-[10px] p-[4px]'>
                    {stackItem.icon}
                    <span>
                      {stackItem.content} {stackItem.suffix}
                    </span>
                  </div>
                ),
            )}
          </div>
          <div className='h-[1px] bg-stroke' />
        </Fragment>
      )}
      {(realEstateId || createdAt) && (
        <div className='flex min-h-[24px] items-center space-x-[16px]'>
          {realEstateId && (
            <div className='flex items-center space-x-[6px]'>
              <span>{realEstateId}</span>
              <button
                type='button'
                onClick={() => {
                  realEstateId && copyText(realEstateId);
                }}
              >
                <IconCopy className='min-h-[24px] min-w-[24px] text-primary' />
              </button>
            </div>
          )}
          {createdAt && (
            <Fragment>
              {!isEmpty(realEstateId) && !isEmpty(createdAt) && (
                <span className='text-text2'>{'\u2022'}</span>
              )}
              <div className='flex items-center space-x-[6px]'>
                <IconCalendar className='h-[20px] min-w-[20px]' />
                <span>{timeSince(moment(createdAt).toDate())}</span>
              </div>
            </Fragment>
          )}
        </div>
      )}
      <ToastStack toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default MainContent;
